// RisingResults — search input that stays pinned to its layout
// position; results overlay rises ABOVE the input into the available
// space above. Replaces the bottom-sheet picker for search-driven
// canonical/brand pickers.
//
// Why this exists:
//   - Old pattern (NameTypeaheadList + MCMPickerSheet) ran a 30-frame
//     getBoundingClientRect settlement loop because the input position
//     drifted as the keyboard opened. That's the lag.
//   - Bottom-sheet pickers cover the form below them — the user loses
//     the chip row below the input while picking.
//   - User explicitly asked: "search must remain at the same height
//     so it comes up to the input field not the other way around."
//
// How it works:
//   - The input element renders inline in the parent layout (no
//     positioning tricks). Caller passes a ref to it.
//   - RisingResults registers a visualViewport listener and an
//     IntersectionObserver fallback to track the input's screen
//     position WITHOUT polling.
//   - When the input is focused (or has a non-empty value), results
//     render in a position:fixed overlay anchored to the bottom of
//     the input's bounding rect. Overlay's max-height = (input.top -
//     safeAreaTop - 8px) so it never extends behind the keyboard.
//   - No backdrop. The form below the input remains visible and
//     interactive (user can tap an axis chip without dismissing).
//   - Dismiss: Esc, blur (with 100ms tap-window), or tap a result.
//
// Performance:
//   - Single 180ms ease-out spring; no layout thrash.
//   - Result list recycles via key; no re-mount on filter change.
//   - Input position recomputed on visualViewport.resize/scroll
//     (not on every keystroke).

import { useEffect, useLayoutEffect, useRef, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { font, color, radius, space } from "../../../lib/mcmTokens";

const OVERLAY_GAP = 8;          // px between input top and overlay bottom
const SAFE_AREA_FALLBACK = 44;  // when env() unavailable
const KEYBOARD_GAP = 4;         // px breathing room above keyboard

// useInputAnchor — tracks the position of the passed-in input ref.
// Returns { top, left, width, height } in viewport coordinates.
// Updates on:
//   - mount
//   - window resize
//   - visualViewport resize/scroll (keyboard open/close)
//   - layout shift (via ResizeObserver on the input itself)
function useInputAnchor(inputRef) {
  const [rect, setRect] = useState(null);

  useLayoutEffect(() => {
    const el = inputRef?.current;
    if (!el) return;

    const measure = () => {
      const r = el.getBoundingClientRect();
      setRect({
        top: r.top,
        left: r.left,
        width: r.width,
        height: r.height,
      });
    };
    measure();

    // ResizeObserver catches layout shifts (parent grows, etc)
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }

    // visualViewport catches iOS keyboard open/close
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener("resize", measure);
      vv.addEventListener("scroll", measure);
    }
    window.addEventListener("resize", measure);

    return () => {
      ro?.disconnect();
      if (vv) {
        vv.removeEventListener("resize", measure);
        vv.removeEventListener("scroll", measure);
      }
      window.removeEventListener("resize", measure);
    };
  }, [inputRef]);

  return rect;
}

// useViewportHeight — returns the actually-visible inner height.
// On iOS with the keyboard open, window.innerHeight stays the full
// screen but visualViewport.height shrinks to the visible portion.
function useViewportHeight() {
  const [h, setH] = useState(() => {
    if (typeof window === "undefined") return 0;
    return window.visualViewport?.height || window.innerHeight;
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    const vv = window.visualViewport;
    const update = () => setH(vv ? vv.height : window.innerHeight);
    if (vv) {
      vv.addEventListener("resize", update);
      vv.addEventListener("scroll", update);
    }
    window.addEventListener("resize", update);
    return () => {
      if (vv) {
        vv.removeEventListener("resize", update);
        vv.removeEventListener("scroll", update);
      }
      window.removeEventListener("resize", update);
    };
  }, []);
  return h;
}

// safeAreaTop — best-effort env(safe-area-inset-top) read. Returns 0
// in browsers without env() or when the value is unset.
function getSafeAreaTop() {
  if (typeof window === "undefined") return 0;
  // CSS env() values aren't directly readable; render an offscreen
  // probe and measure. We only do this once per mount.
  const probe = document.createElement("div");
  probe.style.position = "fixed";
  probe.style.top = "env(safe-area-inset-top, 0px)";
  probe.style.left = "0";
  probe.style.width = "0";
  probe.style.height = "0";
  probe.style.visibility = "hidden";
  document.body.appendChild(probe);
  const rect = probe.getBoundingClientRect();
  const v = rect.top;
  document.body.removeChild(probe);
  return Number.isFinite(v) && v > 0 ? v : 0;
}

/**
 * RisingResults — wraps an existing input element and renders matching
 * results in a floating overlay above it.
 *
 * Props:
 *   inputRef     — required. Ref to the input element this anchors to.
 *   open         — required. Whether the overlay should be visible.
 *                  Caller controls (typically: focused || value.length > 0).
 *   onClose      — required. Caller closes when user taps outside,
 *                  presses Esc, or picks a result.
 *   items        — array of result rows. Caller filters BEFORE passing
 *                  (so RisingResults stays render-only).
 *   getKey       — (item) => stable key. Default: item.id.
 *   renderItem   — (item, isActive) => ReactNode. Caller controls row UI.
 *   onPick       — (item) => void. Fires on row tap; caller closes.
 *   header       — optional ReactNode rendered above the list (e.g.,
 *                  "Pinned canonical" or "Top match").
 *   emptyState   — optional ReactNode when items.length === 0.
 *   maxItems     — soft cap for vertical scroll. Default: list grows
 *                  to its natural max-height.
 */
export default function RisingResults({
  inputRef,
  open,
  onClose,
  items = [],
  getKey = (it) => it?.id,
  renderItem,
  onPick,
  header = null,
  emptyState = null,
  maxItems = null,
  // rowHeight + maxRowsVisible cap how tall the overlay can grow.
  // Default of 40px × 5 = ~200px before scroll kicks in. Callers
  // that want a different cadence can override either.
  rowHeight = 40,
  maxRowsVisible = 5,
}) {
  const anchor = useInputAnchor(inputRef);
  const viewportH = useViewportHeight();
  const [safeTop, setSafeTop] = useState(SAFE_AREA_FALLBACK);
  const overlayRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(-1);

  useLayoutEffect(() => {
    setSafeTop(getSafeAreaTop() || SAFE_AREA_FALLBACK);
  }, []);

  // Esc to dismiss
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => Math.min(items.length - 1, i + 1));
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => Math.max(0, i - 1));
      }
      if (e.key === "Enter" && activeIdx >= 0) {
        e.preventDefault();
        const it = items[activeIdx];
        if (it) onPick?.(it);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, items, activeIdx, onPick]);

  // Tap outside dismisses. We listen on document and check whether the
  // tap landed inside the overlay or the input. If neither, close.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e) => {
      const overlayEl = overlayRef.current;
      const inputEl = inputRef?.current;
      if (overlayEl && overlayEl.contains(e.target)) return;
      if (inputEl && inputEl.contains(e.target)) return;
      onClose?.();
    };
    // Slight delay so the same tap that opens us doesn't immediately
    // close (the focus event and the document tap fire in quick
    // succession on iOS).
    const t = setTimeout(() => {
      document.addEventListener("pointerdown", onPointerDown);
    }, 60);
    return () => {
      clearTimeout(t);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [open, onClose, inputRef]);

  // Reset active index when items change
  useEffect(() => {
    setActiveIdx(items.length > 0 ? 0 : -1);
  }, [items.length]);

  // Compute overlay position from anchor + viewport. The overlay sits
  // ABOVE the input; its bottom edge is at `anchor.top - GAP`. Its top
  // edge is at `safeAreaTop + 8px` (so it never paints behind the
  // status bar). Max height is the gap between those.
  const overlayStyle = useMemo(() => {
    if (!anchor) return null;
    const bottomEdge = anchor.top - OVERLAY_GAP;
    const topEdge = safeTop + 8;
    // Hard cap the overlay so it doesn't dominate the screen with two
    // results stretched across half the viewport. rowHeight × N rows +
    // padding gives a compact dropdown the user can scroll through.
    const visibleRows = Math.max(1, maxRowsVisible);
    const compactCap = rowHeight * visibleRows + 16; // 8px top + 8px bottom padding
    const availableH = Math.max(80, bottomEdge - topEdge);
    const maxH = Math.min(compactCap, availableH);
    return {
      position: "fixed",
      left: anchor.left,
      width: anchor.width,
      bottom: `calc(100vh - ${bottomEdge}px)`,
      maxHeight: maxH,
      zIndex: 1300,
      pointerEvents: "auto",
    };
  }, [anchor, safeTop, viewportH, rowHeight, maxRowsVisible]);

  if (!overlayStyle) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          ref={overlayRef}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 12 }}
          transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
          style={{
            ...overlayStyle,
            display: "flex",
            flexDirection: "column",
            background: color.cream || "#f5efe1",
            border: `1px solid ${color.hairline || "rgba(0,0,0,0.12)"}`,
            borderRadius: radius?.field || 12,
            boxShadow: "0 -8px 32px rgba(0,0,0,0.18), 0 -2px 8px rgba(0,0,0,0.08)",
            overflow: "hidden",
            // backdropFilter improves the rising-from-form feel without
            // a full backdrop. The form below remains visible.
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
          }}
        >
          {header && (
            <div style={{
              padding: "10px 14px 8px",
              borderBottom: `1px solid ${color.hairline || "rgba(0,0,0,0.08)"}`,
              fontFamily: font.mono,
              fontSize: 10,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: color.inkMuted || "#6b6357",
            }}>
              {header}
            </div>
          )}
          <div style={{
            flex: 1,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            padding: 4,
          }}>
            {items.length === 0 && emptyState && (
              <div style={{
                padding: "16px 14px",
                fontFamily: font.detail,
                fontStyle: "italic",
                fontSize: 13,
                color: color.inkMuted || "#6b6357",
                textAlign: "center",
              }}>
                {emptyState}
              </div>
            )}
            {(maxItems ? items.slice(0, maxItems) : items).map((it, i) => {
              const isActive = i === activeIdx;
              return (
                <button
                  key={getKey(it) ?? i}
                  onClick={(e) => {
                    e.preventDefault();
                    onPick?.(it);
                  }}
                  onPointerEnter={() => setActiveIdx(i)}
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    padding: 0,
                    background: isActive
                      ? (color.glassFillLite || "rgba(255,255,255,0.06)")
                      : "transparent",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  {renderItem ? renderItem(it, isActive) : (
                    <div style={{
                      padding: "10px 12px",
                      fontFamily: font.detail,
                      fontStyle: "italic",
                      fontSize: 14,
                      color: color.ink || "#1a1815",
                    }}>
                      {it.label || it.name || String(it)}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
